import {CaregiverMode} from '../../../../../../enums/caregiver-mode';
import ApplicationUsersProfessionalCaregiverOfficeScreen from './application-users.professional-caregiver.screen';
import {Redirect, Route, useRouteMatch, Switch, useParams, useHistory} from 'react-router-dom';
import {useEffect, useState} from 'react';
import ApplicationDetailsProfessionalCaregiverOfficeScreen from './application-details.professional-caregiver.screen';
import {useOffice} from '../../../../../../contexts/office/context';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {ApplicationsRepository} from '../../../../../../repositories/applications';
import {Application} from '../../../../../../backend/interfaces/application';
import {translations} from '../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';

interface ApplicationProfessionalCaregiverOfficeScreenProps {
  canWrite: boolean;
  onApplicationChange: (id: string) => void;
}

export default function ApplicationProfessionalCaregiverOfficeScreen({
  canWrite,
  onApplicationChange,
}: ApplicationProfessionalCaregiverOfficeScreenProps) {
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  const {t} = useTranslation();
  let {applicationId, entityId}: {applicationId: string; entityId: string} = useParams();
  const [sendAppId, setSendAppId] = useState(false);
  let {path, url} = useRouteMatch();
  let history = useHistory();

  useEffect(() => {
    if (sendAppId) {
      onApplicationChange(applicationId);
      setSendAppId(false);
    }
  }, [applicationId, onApplicationChange, sendAppId]);

  useEffect(() => {
    if (applicationId) {
      setSendAppId(true);
    }
  }, [applicationId]);

  useEffect(() => {
    const getApplication = async () => {
      if (applicationId) {
        try {
          // Get application
          // Note: Don't localize because we need the original model for the application form
          const application: Application = await ApplicationsRepository.instance.getByIdForEntity(
            entityId,
            applicationId,
            false
          );

          if (application) {
            // Set application for read only
            officeContext.setApplicationForm(application);
          } else {
            // Throw error
            throw new Error('Empty application.');
          }
        } catch (error) {
          // Log
          console.debug('🔴 Could not get application.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.application_tabs.errors.application_does_not_exist_error),
          });

          // Redirect
          history.push(`${url.replace(`/${applicationId}`, '')}`);
        }
      } else {
        // Log
        console.debug('🔴 Could not get application.');

        // Show toast error
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.application_tabs.errors.application_does_not_exist_error),
        });

        // Redirect
        history.push(`${url.replace('/create', '')}`);
      }
    };

    // Get application
    getApplication();

    // Clean up
    return () => {
      // Clear application
      officeContext.clearApplicationForm();

      // Log
      console.debug('🧹 Application form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  return (
    <Switch>
      {/* Professional caregiver - View application details */}
      <Route exact path={`${path}/details`}>
        <ApplicationDetailsProfessionalCaregiverOfficeScreen canWrite={canWrite} />
      </Route>
      {/* Professional caregiver - Application users */}
      <Route exact path={`${path}/users`}>
        <ApplicationUsersProfessionalCaregiverOfficeScreen
          canWrite={canWrite}
          caregiverMode={CaregiverMode.Professional}
        />
      </Route>

      {/** Default route */}
      <Route path={`${path}`}>
        <Redirect to={`${url}/users`} />
      </Route>
    </Switch>
  );

  /* return (
    <Box sx={boxSx}>
      <ApplicationTabs caregiverMode={CaregiverMode.Professional} canWrite={canWrite} />
    </Box>
  ); */
}
